import React, { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Download, Search, Eye, Trash2, Phone, MessageCircle, Mail } from "lucide-react";

const STATUSES = ["New", "Contacted", "Interested", "Site Visit Scheduled", "Won", "Closed"];
const STATUS_COLORS = {
  "New": "bg-blue-50 text-blue-700", "Contacted": "bg-amber-50 text-amber-700", "Interested": "bg-purple-50 text-purple-700",
  "Site Visit Scheduled": "bg-cyan-50 text-cyan-700", "Won": "bg-emerald-50 text-emerald-700", "Closed": "bg-gray-100 text-gray-600",
};

export default function AdminLeads() {
  const [leads, setLeads] = useState(null);
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState("");

  const load = () => {
    const params = new URLSearchParams();
    if (status !== "all") params.set("status", status);
    if (search) params.set("search", search);
    api.get(`/admin/leads?${params.toString()}`).then((r) => setLeads(r.data)).catch(() => setLeads([]));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [status]);

  const updateStatus = async (id, s) => { await api.put(`/admin/leads/${id}/status`, { status: s }); toast.success("Status updated"); load(); if (detail?.id === id) setDetail({ ...detail, status: s }); };
  const addNote = async () => { if (!note.trim()) return; const { data } = await api.post(`/admin/leads/${detail.id}/notes`, { note }); setDetail(data); setNote(""); toast.success("Note added"); load(); };
  const del = async (id) => { if (!window.confirm("Delete this lead?")) return; await api.delete(`/admin/leads/${id}`); toast.success("Deleted"); setDetail(null); load(); };
  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/leads/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "mathura_leads.csv"; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  return (
    <div className="p-6 md:p-10">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div><h1 className="font-display text-3xl text-navy">Leads</h1><p className="text-muted-foreground mt-1">Manage and track your enquiries.</p></div>
        <Button onClick={exportCsv} data-testid="export-csv-btn" className="bg-navy text-white hover:bg-navy-light"><Download className="w-4 h-4 mr-1" /> Export CSV</Button>
      </div>

      <div className="flex gap-3 mb-5 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Search name, mobile, email, project..." value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} className="pl-10 bg-white" data-testid="lead-search" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[200px] bg-white" data-testid="lead-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Statuses</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <Button variant="outline" onClick={load} className="border-navy text-navy">Search</Button>
      </div>

      {leads === null ? <Skeleton className="h-64 rounded-2xl" /> : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted text-navy"><tr>
              <th className="text-left p-4">Name</th><th className="text-left p-4">Contact</th><th className="text-left p-4">Type</th><th className="text-left p-4">Project</th><th className="text-left p-4">Source</th><th className="text-left p-4">Status</th><th className="text-right p-4">Actions</th>
            </tr></thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id} className="border-t border-gray-100" data-testid={`lead-row-${l.id}`}>
                  <td className="p-4"><p className="font-semibold text-navy">{l.full_name}</p><p className="text-xs text-muted-foreground">{new Date(l.created_at).toLocaleString()}</p></td>
                  <td className="p-4 text-muted-foreground text-xs">{l.mobile}<br />{l.email}</td>
                  <td className="p-4 text-xs">{l.lead_type}</td>
                  <td className="p-4 text-xs text-navy">{l.project_name || "-"}</td>
                  <td className="p-4 text-xs text-muted-foreground">{l.source_page || "-"}</td>
                  <td className="p-4">
                    <Select value={l.status} onValueChange={(s) => updateStatus(l.id, s)}>
                      <SelectTrigger className={`h-8 text-xs w-[150px] border-0 ${STATUS_COLORS[l.status] || ""}`} data-testid={`lead-status-${l.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td className="p-4"><div className="flex justify-end gap-1">
                    <Button size="icon" variant="ghost" onClick={() => { setDetail(l); setNote(""); }} data-testid={`view-lead-${l.id}`}><Eye className="w-4 h-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => del(l.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                  </div></td>
                </tr>
              ))}
              {leads.length === 0 && <tr><td colSpan={7} className="p-10 text-center text-muted-foreground">No leads found.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="lead-detail-dialog">
          {detail && (
            <>
              <DialogHeader><DialogTitle className="font-display text-navy">{detail.full_name}</DialogTitle></DialogHeader>
              <div className="flex gap-2">
                <a href={`tel:${detail.mobile}`} className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-lg border border-navy text-navy text-sm"><Phone className="w-4 h-4" /> Call</a>
                <a href={`https://wa.me/${detail.mobile.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-lg bg-whatsapp text-white text-sm"><MessageCircle className="w-4 h-4" /> WhatsApp</a>
                {detail.email && <a href={`mailto:${detail.email}`} className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-lg border border-navy text-navy text-sm"><Mail className="w-4 h-4" /> Email</a>}
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                {[["Mobile", detail.mobile], ["Email", detail.email], ["Type", detail.lead_type], ["Project", detail.project_name], ["Location", detail.preferred_location], ["Property", detail.property_type], ["BHK", detail.bhk], ["Budget", detail.budget], ["Timeline", detail.timeline], ["Source Page", detail.source_page], ["UTM Source", detail.utm_source], ["UTM Campaign", detail.utm_campaign], ["Referrer", detail.referrer]].map(([k, v]) => v ? <div key={k}><p className="text-xs text-muted-foreground">{k}</p><p className="text-navy break-all">{v}</p></div> : null)}
              </div>
              {detail.message && <div><p className="text-xs text-muted-foreground">Message</p><p className="text-navy text-sm">{detail.message}</p></div>}
              <div>
                <p className="text-sm font-semibold text-navy mb-2">Internal Notes</p>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {(detail.notes || []).map((n, i) => <div key={i} className="p-2 rounded-lg bg-muted text-xs"><p className="text-navy">{n.text}</p><p className="text-muted-foreground mt-1">{n.by} · {new Date(n.at).toLocaleString()}</p></div>)}
                  {(detail.notes || []).length === 0 && <p className="text-xs text-muted-foreground">No notes yet.</p>}
                </div>
                <div className="flex gap-2 mt-3">
                  <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note..." rows={2} className="bg-gray-50" data-testid="lead-note-input" />
                  <Button onClick={addNote} data-testid="add-note-btn" className="bg-gold text-navy hover:bg-gold-dark self-end">Add</Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
